import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  MessageSquare, Gift, Video, BookOpen, ClipboardList, 
  Trophy, Target, Sparkles, Users, User, Eye,
  ArrowRight, ArrowLeft, Loader2, Wand2, Plus, Trash2,
  Clock, Calendar, CheckCircle, Mic, Camera, Mail, Bell, Shield, Megaphone
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { Json } from '@/integrations/supabase/types';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

interface Worker {
  id: string;
  display_name: string;
  team: string;
}

interface Cluster {
  id: string;
  cluster_name: string;
}

interface SurveyQuestion {
  id: string;
  question: string;
  type: 'scale' | 'multiple_choice' | 'text';
  options?: string[];
  required: boolean;
}

interface ChallengeConfig {
  goal: string;
  metric: string;
  targetValue: number;
  durationDays: number;
}

interface PeerObservationConfig {
  behaviorToObserve: string;
  checklistItems: string[];
  photoRequired: boolean;
}

interface SafetyMessageConfig {
  topic: string;
  promptQuestions: string[];
  maxDuration: number;
}

interface ClusterChallengeConfig {
  goal: string;
  mode: 'team_vs_team' | 'all_together';
  durationDays: number;
  targetMetric: string;
  targetValue: number;
  pointsPerAction: number;
}

interface CommitmentConfig {
  statement: string;
  duration: 'daily' | 'weekly' | 'one_time';
}

// Popup-specific content configs
interface NormNudgeContent {
  statistic: string;
  dataSource: 'automatic' | 'manual';
  manualValue?: string;
}

interface RecognitionContent {
  achievementType: 'badge' | 'milestone' | 'streak';
  reason: string;
  badgeIcon?: string;
}

interface SuccessStoryContent {
  isAnonymous: boolean;
  peerName?: string;
  storyDescription: string;
  photoUrl?: string;
}

interface VideoMessageContent {
  videoTitle: string;
  videoUrl: string;
  thumbnailUrl?: string;
  callToAction: string;
}

// Pre-fill data from InterventionRecommendations
interface PrefillData {
  deliveryMethod?: string;
  category?: string;
  targetType?: string;
  targetWorkerId?: string;
  title?: string;
  message?: string;
  xpReward?: number;
  suggestionId?: string;
  structuredContent?: {
    videoTopic?: string;
    maxDuration?: number;
    observationChecklist?: string[];
    commitmentTemplate?: string;
    commitmentDuration?: string;
    challengeGoal?: string;
    challengeMetric?: string;
    challengeTarget?: number;
    challengeDuration?: number;
    surveyQuestions?: any[];
    normNudge?: any;
    recognition?: any;
    successStory?: any;
    videoMessage?: any;
  };
}

const DELIVERY_METHODS = [
  { id: 'popup', label: 'Popup', icon: <MessageSquare className="h-5 w-5" />, description: 'Auto-show on home page' },
  { id: 'quest', label: 'Quest', icon: <Gift className="h-5 w-5" />, description: 'Gamified task with XP' },
  { id: 'banner', label: 'Banner', icon: <Mail className="h-5 w-5" />, description: 'Daily message banner on home' },
];

const POPUP_CATEGORIES = [
  { id: 'norm_nudge', label: 'Norm Nudge', icon: <Target className="h-4 w-4" />, description: 'Social proof messaging', needsSetup: true },
  { id: 'recognition', label: 'Recognition', icon: <Sparkles className="h-4 w-4" />, description: 'Acknowledge achievement', needsSetup: true },
  { id: 'success_story', label: 'Success Story', icon: <Eye className="h-4 w-4" />, description: 'Share peer success', needsSetup: true },
  { id: 'video_message', label: 'Video Message', icon: <Video className="h-4 w-4" />, description: 'Video content', needsSetup: true },
];

const QUEST_CATEGORIES = [
  { id: 'survey', label: 'Survey', icon: <ClipboardList className="h-4 w-4" />, description: 'Collect feedback', needsSetup: true },
  { id: 'challenge', label: 'Challenge', icon: <Trophy className="h-4 w-4" />, description: 'Behavior change challenge', needsSetup: true },
  { id: 'commitment', label: 'Commitment', icon: <Target className="h-4 w-4" />, description: 'Implementation intentions', needsSetup: true },
  { id: 'peer_observation', label: 'Peer Observation', icon: <Eye className="h-4 w-4" />, description: 'Observe and report safe behavior', needsSetup: true },
  { id: 'record_safety_message', label: 'Record Safety Message', icon: <Mic className="h-4 w-4" />, description: 'Record video sharing safety tip', needsSetup: true },
  { id: 'cluster_challenge', label: 'Cluster Challenge', icon: <Users className="h-4 w-4" />, description: 'Team-based behavior challenge', needsSetup: true },
  { id: 'micro_learning', label: 'Micro Learning', icon: <BookOpen className="h-4 w-4" />, description: 'Interactive learning module', needsSetup: false, redirectTo: '/admin/create-learning' },
];

const BANNER_CATEGORIES = [
  { id: 'motivation', label: 'Motivation', icon: <Sparkles className="h-4 w-4" />, description: 'Inspirational message', needsSetup: false },
  { id: 'safety_tip', label: 'Safety Tip', icon: <Shield className="h-4 w-4" />, description: 'Quick safety reminder', needsSetup: false },
  { id: 'reminder', label: 'Reminder', icon: <Bell className="h-4 w-4" />, description: 'Task or compliance reminder', needsSetup: false },
  { id: 'recognition', label: 'Recognition', icon: <Trophy className="h-4 w-4" />, description: 'Acknowledge achievement', needsSetup: false },
  { id: 'announcement', label: 'Announcement', icon: <Megaphone className="h-4 w-4" />, description: 'Company announcement', needsSetup: false },
];

const getCategories = (method: string) => {
  if (method === 'popup') return POPUP_CATEGORIES;
  if (method === 'quest') return QUEST_CATEGORIES;
  if (method === 'banner') return BANNER_CATEGORIES;
  return [];
};

const TARGET_TYPES = [
  { id: 'individual', label: 'Individual', icon: <User className="h-4 w-4" /> },
  { id: 'cluster', label: 'Cluster', icon: <Users className="h-4 w-4" /> },
  { id: 'team', label: 'Team', icon: <Users className="h-4 w-4" /> },
  { id: 'all', label: 'All Workers', icon: <Users className="h-4 w-4" /> },
];

const ACHIEVEMENT_TYPES = [
  { id: 'badge', label: 'Badge', description: 'Award a badge' },
  { id: 'milestone', label: 'Milestone', description: 'Celebrate a milestone' },
  { id: 'streak', label: 'Streak', description: 'Recognize streak achievement' },
];

const BADGE_ICONS = ['🏆', '⭐', '🎯', '🛡️', '🔥', '💪', '🌟', '🏅'];

export default function CreateIntervention() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);

  // Pre-fill data from InterventionRecommendations
  const prefillData = (location.state as { prefill?: PrefillData })?.prefill;
  const [suggestionId, setSuggestionId] = useState<string | null>(null);

  // Form state
  const [deliveryMethod, setDeliveryMethod] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [targetType, setTargetType] = useState<string>('');
  const [targetWorkerId, setTargetWorkerId] = useState<string>('');
  const [targetClusterId, setTargetClusterId] = useState<string>('');
  const [targetTeam, setTargetTeam] = useState<string>('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [xpReward, setXpReward] = useState(10);
  const [useAiContent, setUseAiContent] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');

  // Category-specific state
  const [surveyQuestions, setSurveyQuestions] = useState<SurveyQuestion[]>([
    { id: '1', question: '', type: 'scale', required: true }
  ]);
  const [challengeConfig, setChallengeConfig] = useState<ChallengeConfig>({
    goal: '',
    metric: 'days_without_violation',
    targetValue: 7,
    durationDays: 7
  });

  // New quest category configs
  const [peerObservationConfig, setPeerObservationConfig] = useState<PeerObservationConfig>({
    behaviorToObserve: '',
    checklistItems: [''],
    photoRequired: true
  });
  const [safetyMessageConfig, setSafetyMessageConfig] = useState<SafetyMessageConfig>({
    topic: '',
    promptQuestions: [''],
    maxDuration: 60
  });
  const [clusterChallengeConfig, setClusterChallengeConfig] = useState<ClusterChallengeConfig>({
    goal: '',
    mode: 'all_together',
    durationDays: 7,
    targetMetric: 'hazard_reports',
    targetValue: 10,
    pointsPerAction: 5
  });
  const [commitmentConfig, setCommitmentConfig] = useState<CommitmentConfig>({
    statement: '',
    duration: 'one_time'
  });

  // Popup category-specific content
  const [normNudgeContent, setNormNudgeContent] = useState<NormNudgeContent>({
    statistic: '',
    dataSource: 'manual',
    manualValue: ''
  });
  const [recognitionContent, setRecognitionContent] = useState<RecognitionContent>({
    achievementType: 'badge',
    reason: '',
    badgeIcon: '🏆'
  });
  const [successStoryContent, setSuccessStoryContent] = useState<SuccessStoryContent>({
    isAnonymous: false,
    peerName: '',
    storyDescription: '',
    photoUrl: ''
  });
  const [videoMessageContent, setVideoMessageContent] = useState<VideoMessageContent>({
    videoTitle: '',
    videoUrl: '',
    thumbnailUrl: '',
    callToAction: ''
  });

  // Timing state
  const [deliveryTiming, setDeliveryTiming] = useState<'now' | 'scheduled'>('now');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');

  // Data
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [teams, setTeams] = useState<string[]>([]);

  const currentCategories = getCategories(deliveryMethod);
  const categoryNeedsSetup = currentCategories.find(c => c.id === category)?.needsSetup || false;
  
  // Updated step flow: Setup categories now have 5 steps (no separate content step)
  // For quest setup categories: Delivery → Category → Setup (with title/xp) → Target → Timing
  // For popup setup categories: Delivery → Category → Setup (with title/xp) → Target → Timing
  // For banner/non-setup: Delivery → Category → Target → Content → Timing
  const totalSteps = categoryNeedsSetup ? 5 : 5;
  const stepLabels = categoryNeedsSetup 
    ? ['Delivery', 'Category', 'Setup', 'Target', 'Timing']
    : ['Delivery', 'Category', 'Target', 'Content', 'Timing'];

  useEffect(() => {
    loadData();
  }, []);

  // Apply pre-fill data from InterventionRecommendations
  useEffect(() => {
    if (prefillData) {
      if (prefillData.deliveryMethod) setDeliveryMethod(prefillData.deliveryMethod);
      if (prefillData.category) setCategory(prefillData.category);
      if (prefillData.targetType) setTargetType(prefillData.targetType);
      if (prefillData.targetWorkerId) setTargetWorkerId(prefillData.targetWorkerId);
      if (prefillData.title) setTitle(prefillData.title);
      if (prefillData.message) setMessage(prefillData.message);
      if (prefillData.xpReward) setXpReward(prefillData.xpReward);
      if (prefillData.suggestionId) setSuggestionId(prefillData.suggestionId);

      // Apply structured content to category-specific configs
      if (prefillData.structuredContent) {
        const sc = prefillData.structuredContent;
        
        // Quest types
        if (sc.videoTopic) {
          setSafetyMessageConfig(prev => ({ 
            ...prev, 
            topic: sc.videoTopic!,
            maxDuration: sc.maxDuration || 60
          }));
        }
        if (sc.observationChecklist) {
          setPeerObservationConfig(prev => ({
            ...prev,
            checklistItems: sc.observationChecklist!,
            behaviorToObserve: prefillData.message || ''
          }));
        }
        if (sc.commitmentTemplate) {
          setCommitmentConfig(prev => ({
            ...prev,
            statement: sc.commitmentTemplate!,
            duration: (sc.commitmentDuration as 'daily' | 'weekly' | 'one_time') || 'one_time'
          }));
        }
        if (sc.challengeGoal) {
          setChallengeConfig(prev => ({
            ...prev,
            goal: sc.challengeGoal!,
            metric: sc.challengeMetric || 'days_without_violation',
            targetValue: sc.challengeTarget || 7,
            durationDays: sc.challengeDuration || 7
          }));
        }
        if (sc.surveyQuestions) {
          setSurveyQuestions(sc.surveyQuestions);
        }

        // Popup types
        if (sc.normNudge) {
          setNormNudgeContent(prev => ({ ...prev, ...sc.normNudge }));
        }
        if (sc.recognition) {
          setRecognitionContent(prev => ({ ...prev, ...sc.recognition }));
        }
        if (sc.successStory) {
          setSuccessStoryContent(prev => ({ ...prev, ...sc.successStory }));
        }
        if (sc.videoMessage) {
          setVideoMessageContent(prev => ({ ...prev, ...sc.videoMessage }));
        }
      }

      // Auto-skip to appropriate step
      if (prefillData.deliveryMethod && prefillData.category) {
        setStep(3); // Go to setup or target step
      }
    }
  }, [prefillData]);

  const loadData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/admin/login');
      return;
    }

    const { data: profile } = await supabase
      .from('worker_profiles')
      .select('company_id')
      .eq('user_id', user.id)
      .maybeSingle();

    if (profile?.company_id) {
      setCompanyId(profile.company_id);
      
      const { data: workersData } = await supabase
        .from('worker_profiles')
        .select('id, display_name, team')
        .eq('company_id', profile.company_id);
      setWorkers(workersData || []);

      const uniqueTeams = [...new Set((workersData || []).map(w => w.team))].filter(Boolean);
      setTeams(uniqueTeams);

      const { data: clustersData } = await supabase
        .from('worker_clusters')
        .select('id, cluster_name')
        .eq('company_id', profile.company_id);
      setClusters(clustersData || []);
    }
  };

  const handleCategorySelect = (catId: string) => {
    const selectedCategory = currentCategories.find(c => c.id === catId);
    if (selectedCategory && 'redirectTo' in selectedCategory && selectedCategory.redirectTo) {
      navigate(selectedCategory.redirectTo as string);
      return;
    }
    setCategory(catId);
  };

  const handleGenerateContent = async () => {
    if (!aiPrompt) {
      toast.error('Please enter a prompt for AI content generation');
      return;
    }

    setGeneratingContent(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-intervention-content', {
        body: { category, prompt: aiPrompt, targetType, companyId },
      });

      if (error) throw error;
      if (data?.title) setTitle(data.title);
      if (data?.message) setMessage(data.message);
      toast.success('Content generated!');
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error('Failed to generate content');
    } finally {
      setGeneratingContent(false);
    }
  };

  const addSurveyQuestion = () => {
    setSurveyQuestions([...surveyQuestions, {
      id: Date.now().toString(),
      question: '',
      type: 'scale',
      required: true
    }]);
  };

  const removeSurveyQuestion = (id: string) => {
    if (surveyQuestions.length > 1) {
      setSurveyQuestions(surveyQuestions.filter(q => q.id !== id));
    }
  };

  const updateSurveyQuestion = (id: string, updates: Partial<SurveyQuestion>) => {
    setSurveyQuestions(surveyQuestions.map(q => 
      q.id === id ? { ...q, ...updates } : q
    ));
  };

  const addChecklistItem = () => {
    setPeerObservationConfig({
      ...peerObservationConfig,
      checklistItems: [...peerObservationConfig.checklistItems, '']
    });
  };

  const removeChecklistItem = (index: number) => {
    if (peerObservationConfig.checklistItems.length > 1) {
      const items = [...peerObservationConfig.checklistItems];
      items.splice(index, 1);
      setPeerObservationConfig({ ...peerObservationConfig, checklistItems: items });
    }
  };

  const updateChecklistItem = (index: number, value: string) => {
    const items = [...peerObservationConfig.checklistItems];
    items[index] = value;
    setPeerObservationConfig({ ...peerObservationConfig, checklistItems: items });
  };

  const addPromptQuestion = () => {
    setSafetyMessageConfig({
      ...safetyMessageConfig,
      promptQuestions: [...safetyMessageConfig.promptQuestions, '']
    });
  };

  const removePromptQuestion = (index: number) => {
    if (safetyMessageConfig.promptQuestions.length > 1) {
      const questions = [...safetyMessageConfig.promptQuestions];
      questions.splice(index, 1);
      setSafetyMessageConfig({ ...safetyMessageConfig, promptQuestions: questions });
    }
  };

  const updatePromptQuestion = (index: number, value: string) => {
    const questions = [...safetyMessageConfig.promptQuestions];
    questions[index] = value;
    setSafetyMessageConfig({ ...safetyMessageConfig, promptQuestions: questions });
  };

  const handleSubmit = async () => {
    // Banner only requires message, others require title
    if (deliveryMethod === 'banner') {
      if (!message) {
        toast.error('Message is required');
        return;
      }
    } else if (!title) {
      toast.error('Title is required');
      return;
    }

    setLoading(true);
    try {
      let targetWorkerIds: string[] = [];

      if (targetType === 'individual' && targetWorkerId) {
        targetWorkerIds = [targetWorkerId];
      } else if (targetType === 'cluster' && targetClusterId) {
        const { data: cluster } = await supabase
          .from('worker_clusters')
          .select('member_ids')
          .eq('id', targetClusterId)
          .single();
        targetWorkerIds = cluster?.member_ids || [];
      } else if (targetType === 'team' && targetTeam) {
        const { data: teamWorkers } = await supabase
          .from('worker_profiles')
          .select('id')
          .eq('company_id', companyId)
          .eq('team', targetTeam);
        targetWorkerIds = (teamWorkers || []).map(w => w.id);
      } else if (targetType === 'all') {
        const { data: allWorkers } = await supabase
          .from('worker_profiles')
          .select('id')
          .eq('company_id', companyId);
        targetWorkerIds = (allWorkers || []).map(w => w.id);
      }

      if (targetWorkerIds.length === 0) {
        toast.error('No target workers found');
        setLoading(false);
        return;
      }

      let content: Json = {};
      if (category === 'survey') {
        content = { questions: surveyQuestions.filter(q => q.question.trim()) } as unknown as Json;
      } else if (category === 'challenge') {
        content = { challenge: challengeConfig } as unknown as Json;
      } else if (category === 'peer_observation') {
        content = { peerObservation: peerObservationConfig } as unknown as Json;
      } else if (category === 'record_safety_message') {
        content = { safetyMessage: safetyMessageConfig } as unknown as Json;
      } else if (category === 'cluster_challenge') {
        content = { clusterChallenge: clusterChallengeConfig } as unknown as Json;
      } else if (category === 'commitment') {
        content = { commitment: commitmentConfig } as unknown as Json;
      } else if (category === 'norm_nudge') {
        content = { normNudge: normNudgeContent } as unknown as Json;
      } else if (category === 'recognition') {
        content = { recognition: recognitionContent } as unknown as Json;
      } else if (category === 'success_story') {
        content = { successStory: successStoryContent } as unknown as Json;
      } else if (category === 'video_message') {
        content = { videoMessage: videoMessageContent } as unknown as Json;
      }

      let deliveredAt = null;
      if (deliveryTiming === 'now') {
        deliveredAt = new Date().toISOString();
      }

      const scheduledFor = deliveryTiming === 'scheduled' && scheduledDate 
        ? new Date(`${scheduledDate}T${scheduledTime}:00`).toISOString()
        : null;

      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 48);

      if (deliveryMethod === 'quest') {
        const questTypeMap: Record<string, string> = {
          survey: 'survey',
          challenge: 'challenge',
          commitment: 'commitment',
          peer_observation: 'observation',
          record_safety_message: 'recording',
          cluster_challenge: 'team_challenge',
        };
        const questType = questTypeMap[category] || 'learning';

        const { data: templateData, error: templateError } = await supabase
          .from('quest_templates')
          .insert({
            company_id: companyId,
            title,
            description: message || title,
            quest_type: questType,
            intervention_type: category,
            xp_reward: xpReward,
            content,
            duration_hours: 48,
            is_active: true,
          })
          .select('id')
          .single();

        if (templateError) throw templateError;

        const workerQuests = targetWorkerIds.map(workerId => ({
          worker_id: workerId,
          template_id: templateData.id,
          status: 'pending',
          expires_at: expiresAt.toISOString(),
          response_data: {},
        }));

        const { data: questsData, error: questsError } = await supabase
          .from('worker_quests')
          .insert(workerQuests)
          .select('id, worker_id');

        if (questsError) throw questsError;

        // Send push notifications for quest assignments
        if (questsData && questsData.length > 0 && targetWorkerIds.length > 0) {
          try {
            await supabase.functions.invoke('send-push-notification', {
              body: {
                recipientIds: targetWorkerIds,
                title: '📋 Quest Baru!',
                body: `${title}. Selesaikan untuk dapat ${xpReward} XP!`,
                data: {
                  type: 'quest_assigned',
                  quest_id: questsData[0]?.id || '',
                  category: category,
                }
              }
            });
            console.log(`[CreateIntervention] Push sent to ${targetWorkerIds.length} workers`);
          } catch (pushError) {
            console.warn('[CreateIntervention] Push failed:', pushError);
          }
        }

        const interventions = (questsData || []).map(quest => ({
          company_id: companyId,
          delivery_method: deliveryMethod,
          category,
          target_type: targetType,
          target_worker_id: quest.worker_id,
          target_cluster_id: targetType === 'cluster' ? targetClusterId : null,
          target_team: targetType === 'team' ? targetTeam : null,
          trigger_type: 'manual',
          title,
          message: message || null,
          video_url: videoUrl || null,
          xp_reward: xpReward,
          status: deliveryTiming === 'now' ? 'pending' : 'scheduled',
          delivered_at: deliveredAt,
          expires_at: expiresAt.toISOString(),
          content,
          worker_quest_id: quest.id,
        }));

        const { error: interventionError } = await supabase.from('interventions').insert(interventions);
        if (interventionError) throw interventionError;
      } else if (deliveryMethod === 'banner') {
        // For banner/daily message interventions - use 'daily_message' for compatibility
        const bannerInterventions = targetWorkerIds.map(workerId => ({
          company_id: companyId,
          delivery_method: 'daily_message',
          category,
          target_type: targetType,
          target_worker_id: workerId,
          target_cluster_id: targetType === 'cluster' ? targetClusterId : null,
          target_team: targetType === 'team' ? targetTeam : null,
          trigger_type: 'manual',
          title: message || 'Daily Message', // Use message as title for display
          message: message || null,
          xp_reward: 0, // No XP for banner
          status: deliveryTiming === 'now' ? 'pending' : 'scheduled',
          delivered_at: deliveredAt,
          scheduled_for: scheduledFor,
          expires_at: expiresAt.toISOString(),
          content: { bannerCategory: category } as unknown as Json,
        }));

        const { error } = await supabase.from('interventions').insert(bannerInterventions);
        if (error) throw error;
      } else {
        // For popup interventions, map category-specific content to top-level columns
        let popupVideoUrl: string | null = null;
        let popupImageUrl: string | null = null;
        let popupMessage: string | null = message || null;

        if (category === 'video_message') {
          popupVideoUrl = videoMessageContent.videoUrl || null;
          popupImageUrl = videoMessageContent.thumbnailUrl || null;
          if (!popupMessage && videoMessageContent.callToAction) {
            popupMessage = videoMessageContent.callToAction;
          }
        } else if (category === 'success_story') {
          popupImageUrl = successStoryContent.photoUrl || null;
          if (!popupMessage && successStoryContent.storyDescription) {
            popupMessage = successStoryContent.storyDescription;
          }
        } else if (category === 'norm_nudge') {
          if (!popupMessage && normNudgeContent.statistic) {
            popupMessage = normNudgeContent.statistic;
          }
        } else if (category === 'recognition') {
          if (!popupMessage && recognitionContent.reason) {
            popupMessage = recognitionContent.reason;
          }
        }

        const interventions = targetWorkerIds.map(workerId => ({
          company_id: companyId,
          delivery_method: deliveryMethod,
          category,
          target_type: targetType,
          target_worker_id: workerId,
          target_cluster_id: targetType === 'cluster' ? targetClusterId : null,
          target_team: targetType === 'team' ? targetTeam : null,
          trigger_type: 'manual',
          title,
          message: popupMessage,
          video_url: popupVideoUrl || videoUrl || null,
          image_url: popupImageUrl || null,
          xp_reward: xpReward,
          status: deliveryTiming === 'now' ? 'pending' : 'scheduled',
          delivered_at: deliveredAt,
          expires_at: scheduledFor,
          content,
        }));

        const { error } = await supabase.from('interventions').insert(interventions);
        if (error) throw error;
      }

      // Mark suggestion as deployed if this came from InterventionRecommendations
      if (suggestionId) {
        await supabase
          .from('intervention_suggestions')
          .update({ status: 'deployed', updated_at: new Date().toISOString() })
          .eq('id', suggestionId);
      }

      const timingMessage = deliveryTiming === 'now' 
        ? 'and delivered' 
        : `scheduled for ${scheduledDate} ${scheduledTime}`;
      toast.success(`Created ${targetWorkerIds.length} intervention(s) ${timingMessage}`);
      navigate('/admin?tab=intervention');
    } catch (error) {
      console.error('Error creating intervention:', error);
      toast.error('Failed to create intervention');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return !!deliveryMethod;
    if (step === 2) return !!category;

    const bannerHasMessage = !!message.trim();

    if (categoryNeedsSetup) {
      if (step === 3) {
        // Setup step - now also validates title
        if (category === 'survey') {
          return surveyQuestions.some(q => q.question.trim()) && title.trim() !== '';
        }
        if (category === 'challenge') {
          return challengeConfig.goal.trim() !== '' && title.trim() !== '';
        }
        if (category === 'peer_observation') {
          return peerObservationConfig.behaviorToObserve.trim() !== '' &&
                 peerObservationConfig.checklistItems.some(item => item.trim()) &&
                 title.trim() !== '';
        }
        if (category === 'record_safety_message') {
          return safetyMessageConfig.topic.trim() !== '' && title.trim() !== '';
        }
        if (category === 'cluster_challenge') {
          return clusterChallengeConfig.goal.trim() !== '' && title.trim() !== '';
        }
        if (category === 'commitment') {
          return commitmentConfig.statement.trim() !== '' && title.trim() !== '';
        }
        // Popup categories with setup
        if (category === 'norm_nudge') {
          return normNudgeContent.statistic.trim() !== '' && title.trim() !== '';
        }
        if (category === 'recognition') {
          return recognitionContent.reason.trim() !== '' && title.trim() !== '';
        }
        if (category === 'success_story') {
          return successStoryContent.storyDescription.trim() !== '' && title.trim() !== '';
        }
        if (category === 'video_message') {
          return videoMessageContent.videoUrl.trim() !== '' && title.trim() !== '';
        }
        return title.trim() !== '';
      }
      if (step === 4) {
        return !!targetType && (
          targetType === 'all' ||
          (targetType === 'individual' && targetWorkerId) ||
          (targetType === 'cluster' && targetClusterId) ||
          (targetType === 'team' && targetTeam)
        );
      }
      if (step === 5) {
        return deliveryTiming === 'now' || (deliveryTiming === 'scheduled' && scheduledDate);
      }
    } else {
      if (step === 3) {
        return !!targetType && (
          targetType === 'all' ||
          (targetType === 'individual' && targetWorkerId) ||
          (targetType === 'cluster' && targetClusterId) ||
          (targetType === 'team' && targetTeam)
        );
      }
      if (step === 4) return deliveryMethod === 'banner' ? bannerHasMessage : !!title;
      if (step === 5) {
        return deliveryTiming === 'now' || (deliveryTiming === 'scheduled' && scheduledDate);
      }
    }

    return false;
  };

  // Shared title/XP fields component for setup steps
  const renderTitleXpFields = () => (
    <>
      <div className="border-t pt-6 mt-6">
        <div className="space-y-4">
          <div>
            <Label>Quest Title *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter a title for this quest..."
            />
          </div>
          <div>
            <Label>Description (Optional)</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Additional description or instructions..."
              rows={2}
            />
          </div>
          <div>
            <Label>XP Reward</Label>
            <Input
              type="number"
              value={xpReward}
              onChange={(e) => setXpReward(parseInt(e.target.value) || 0)}
              min={0}
              max={100}
            />
          </div>
        </div>
      </div>
    </>
  );

  const renderSetupStep = () => {
    if (category === 'survey') {
      return (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="space-y-6 max-w-2xl mx-auto"
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2">Survey Setup</h2>
            <p className="text-muted-foreground">Add questions for your survey</p>
          </div>

          <div className="space-y-4">
            {surveyQuestions.map((q, idx) => (
              <Card key={q.id} className="p-4">
                <div className="flex items-start gap-3">
                  <Badge variant="outline">{idx + 1}</Badge>
                  <div className="flex-1 space-y-3">
                    <Input
                      value={q.question}
                      onChange={(e) => updateSurveyQuestion(q.id, { question: e.target.value })}
                      placeholder="Enter your question..."
                    />
                    <Select
                      value={q.type}
                      onValueChange={(v) => updateSurveyQuestion(q.id, { type: v as any, options: v === 'multiple_choice' ? ['', ''] : undefined })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="scale">Scale (1-10)</SelectItem>
                        <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                        <SelectItem value="text">Text</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {q.type === 'multiple_choice' && (
                      <div className="space-y-2 mt-3">
                        <Label className="text-sm text-muted-foreground">Answer Options</Label>
                        {(q.options || ['', '']).map((option, optIdx) => (
                          <div key={optIdx} className="flex items-center gap-2">
                            <Badge variant="outline" className="shrink-0 text-xs">{String.fromCharCode(65 + optIdx)}</Badge>
                            <Input
                              value={option}
                              onChange={(e) => {
                                const newOptions = [...(q.options || [])];
                                newOptions[optIdx] = e.target.value;
                                updateSurveyQuestion(q.id, { options: newOptions });
                              }}
                              placeholder={`Option ${String.fromCharCode(65 + optIdx)}`}
                              className="flex-1"
                            />
                            {(q.options || []).length > 2 && (
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => {
                                  const newOptions = [...(q.options || [])];
                                  newOptions.splice(optIdx, 1);
                                  updateSurveyQuestion(q.id, { options: newOptions });
                                }}
                              >
                                <Trash2 className="h-3 w-3 text-destructive" />
                              </Button>
                            )}
                          </div>
                        ))}
                        {(q.options || []).length < 6 && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => {
                              const newOptions = [...(q.options || []), ''];
                              updateSurveyQuestion(q.id, { options: newOptions });
                            }}
                            className="w-full text-xs"
                          >
                            <Plus className="h-3 w-3 mr-1" /> Add Option
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  {surveyQuestions.length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => removeSurveyQuestion(q.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </Card>
            ))}
            <Button variant="outline" onClick={addSurveyQuestion} className="w-full">
              <Plus className="h-4 w-4 mr-2" /> Add Question
            </Button>
          </div>

          {renderTitleXpFields()}
        </motion.div>
      );
    }

    if (category === 'challenge') {
      return (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="space-y-6 max-w-2xl mx-auto"
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2">Challenge Setup</h2>
            <p className="text-muted-foreground">Set up your behavior challenge</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Challenge Goal *</Label>
              <Input
                value={challengeConfig.goal}
                onChange={(e) => setChallengeConfig({ ...challengeConfig, goal: e.target.value })}
                placeholder="e.g., Wear PPE consistently for 7 days"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Target Value</Label>
                <Input
                  type="number"
                  value={challengeConfig.targetValue}
                  onChange={(e) => setChallengeConfig({ ...challengeConfig, targetValue: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Duration (Days)</Label>
                <Input
                  type="number"
                  value={challengeConfig.durationDays}
                  onChange={(e) => setChallengeConfig({ ...challengeConfig, durationDays: parseInt(e.target.value) || 7 })}
                />
              </div>
            </div>
          </div>

          {renderTitleXpFields()}
        </motion.div>
      );
    }

    if (category === 'commitment') {
      return (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="space-y-6 max-w-2xl mx-auto"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Target className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Commitment Setup</h2>
            <p className="text-muted-foreground">Create an implementation intention commitment</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Commitment Statement *</Label>
              <Textarea
                value={commitmentConfig.statement}
                onChange={(e) => setCommitmentConfig({ ...commitmentConfig, statement: e.target.value })}
                placeholder="e.g., Saya berkomitmen untuk selalu memeriksa APD sebelum memulai pekerjaan setiap hari."
                rows={4}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Tip: Use specific, actionable commitments for better results
              </p>
            </div>

            <div>
              <Label>Commitment Duration</Label>
              <Select
                value={commitmentConfig.duration}
                onValueChange={(v) => setCommitmentConfig({ ...commitmentConfig, duration: v as 'daily' | 'weekly' | 'one_time' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_time">One Time</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {renderTitleXpFields()}
        </motion.div>
      );
    }

    if (category === 'peer_observation') {
      return (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="space-y-6 max-w-2xl mx-auto"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Eye className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Peer Observation Setup</h2>
            <p className="text-muted-foreground">Configure what workers should observe</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Behavior to Observe *</Label>
              <Input
                value={peerObservationConfig.behaviorToObserve}
                onChange={(e) => setPeerObservationConfig({ ...peerObservationConfig, behaviorToObserve: e.target.value })}
                placeholder="e.g., Proper PPE usage, Safe lifting techniques"
              />
            </div>

            <div>
              <Label>Observation Checklist</Label>
              <p className="text-sm text-muted-foreground mb-2">Items the observer should check</p>
              <div className="space-y-2">
                {peerObservationConfig.checklistItems.map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Badge variant="outline" className="shrink-0">{index + 1}</Badge>
                    <Input
                      value={item}
                      onChange={(e) => updateChecklistItem(index, e.target.value)}
                      placeholder="e.g., Wearing safety glasses"
                    />
                    {peerObservationConfig.checklistItems.length > 1 && (
                      <Button variant="ghost" size="icon" onClick={() => removeChecklistItem(index)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" onClick={addChecklistItem} className="w-full">
                  <Plus className="h-4 w-4 mr-2" /> Add Checklist Item
                </Button>
              </div>
            </div>

            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Camera className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <Label>Require Photo Evidence</Label>
                    <p className="text-sm text-muted-foreground">Observer must take a photo</p>
                  </div>
                </div>
                <Switch
                  checked={peerObservationConfig.photoRequired}
                  onCheckedChange={(checked) => setPeerObservationConfig({ ...peerObservationConfig, photoRequired: checked })}
                />
              </div>
            </Card>
          </div>

          {renderTitleXpFields()}
        </motion.div>
      );
    }

    if (category === 'record_safety_message') {
      return (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="space-y-6 max-w-2xl mx-auto"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Mic className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Safety Message Setup</h2>
            <p className="text-muted-foreground">Configure the video recording quest</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Topic / Theme *</Label>
              <Input
                value={safetyMessageConfig.topic}
                onChange={(e) => setSafetyMessageConfig({ ...safetyMessageConfig, topic: e.target.value })}
                placeholder="e.g., Importance of wearing safety goggles"
              />
            </div>

            <div>
              <Label>Guiding Questions (Optional)</Label>
              <p className="text-sm text-muted-foreground mb-2">Prompts to help worker record their message</p>
              <div className="space-y-2">
                {safetyMessageConfig.promptQuestions.map((q, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Badge variant="outline" className="shrink-0">{index + 1}</Badge>
                    <Input
                      value={q}
                      onChange={(e) => updatePromptQuestion(index, e.target.value)}
                      placeholder="e.g., Why is this important to you?"
                    />
                    {safetyMessageConfig.promptQuestions.length > 1 && (
                      <Button variant="ghost" size="icon" onClick={() => removePromptQuestion(index)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" onClick={addPromptQuestion} className="w-full">
                  <Plus className="h-4 w-4 mr-2" /> Add Question
                </Button>
              </div>
            </div>

            <div>
              <Label>Max Duration (seconds)</Label>
              <Select
                value={safetyMessageConfig.maxDuration.toString()}
                onValueChange={(v) => setSafetyMessageConfig({ ...safetyMessageConfig, maxDuration: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 seconds</SelectItem>
                  <SelectItem value="60">60 seconds</SelectItem>
                  <SelectItem value="90">90 seconds</SelectItem>
                  <SelectItem value="120">2 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {renderTitleXpFields()}
        </motion.div>
      );
    }

    if (category === 'cluster_challenge') {
      return (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="space-y-6 max-w-2xl mx-auto"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Cluster Challenge Setup</h2>
            <p className="text-muted-foreground">Configure the team challenge</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Challenge Goal *</Label>
              <Input
                value={clusterChallengeConfig.goal}
                onChange={(e) => setClusterChallengeConfig({ ...clusterChallengeConfig, goal: e.target.value })}
                placeholder="e.g., Report 20 hazards as a team this week"
              />
            </div>

            <div>
              <Label>Challenge Mode</Label>
              <RadioGroup
                value={clusterChallengeConfig.mode}
                onValueChange={(v) => setClusterChallengeConfig({ ...clusterChallengeConfig, mode: v as 'team_vs_team' | 'all_together' })}
                className="mt-2"
              >
                <div className="grid grid-cols-2 gap-4">
                  <Card className={`p-4 cursor-pointer ${clusterChallengeConfig.mode === 'all_together' ? 'ring-2 ring-primary' : ''}`}
                    onClick={() => setClusterChallengeConfig({ ...clusterChallengeConfig, mode: 'all_together' })}>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="all_together" />
                      <div>
                        <p className="font-medium">All Together</p>
                        <p className="text-xs text-muted-foreground">Collaborate to reach goal</p>
                      </div>
                    </div>
                  </Card>
                  <Card className={`p-4 cursor-pointer ${clusterChallengeConfig.mode === 'team_vs_team' ? 'ring-2 ring-primary' : ''}`}
                    onClick={() => setClusterChallengeConfig({ ...clusterChallengeConfig, mode: 'team_vs_team' })}>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="team_vs_team" />
                      <div>
                        <p className="font-medium">Team vs Team</p>
                        <p className="text-xs text-muted-foreground">Compete against each other</p>
                      </div>
                    </div>
                  </Card>
                </div>
              </RadioGroup>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Target Metric</Label>
                <Select
                  value={clusterChallengeConfig.targetMetric}
                  onValueChange={(v) => setClusterChallengeConfig({ ...clusterChallengeConfig, targetMetric: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hazard_reports">Hazard Reports</SelectItem>
                    <SelectItem value="checkins">Check-ins Completed</SelectItem>
                    <SelectItem value="quests_completed">Quests Completed</SelectItem>
                    <SelectItem value="days_without_incident">Days Without Incident</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Target Value</Label>
                <Input
                  type="number"
                  value={clusterChallengeConfig.targetValue}
                  onChange={(e) => setClusterChallengeConfig({ ...clusterChallengeConfig, targetValue: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Duration (Days)</Label>
                <Input
                  type="number"
                  value={clusterChallengeConfig.durationDays}
                  onChange={(e) => setClusterChallengeConfig({ ...clusterChallengeConfig, durationDays: parseInt(e.target.value) || 7 })}
                />
              </div>
              <div>
                <Label>Points Per Action</Label>
                <Input
                  type="number"
                  value={clusterChallengeConfig.pointsPerAction}
                  onChange={(e) => setClusterChallengeConfig({ ...clusterChallengeConfig, pointsPerAction: parseInt(e.target.value) || 5 })}
                />
              </div>
            </div>
          </div>

          {renderTitleXpFields()}
        </motion.div>
      );
    }

    // Popup categories with setup
    if (category === 'norm_nudge') {
      return (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="space-y-6 max-w-2xl mx-auto"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Target className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Norm Nudge Setup</h2>
            <p className="text-muted-foreground">Create social proof messaging</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Data Source</Label>
              <RadioGroup
                value={normNudgeContent.dataSource}
                onValueChange={(v) => setNormNudgeContent({ ...normNudgeContent, dataSource: v as 'automatic' | 'manual' })}
                className="mt-2"
              >
                <div className="flex gap-4">
                  <Label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="automatic" />
                    Automatic (from system data)
                  </Label>
                  <Label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="manual" />
                    Manual entry
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div>
              <Label>Statistic / Social Proof Message *</Label>
              <Textarea
                value={normNudgeContent.statistic}
                onChange={(e) => setNormNudgeContent({ ...normNudgeContent, statistic: e.target.value })}
                placeholder="e.g., 85% of your peers always wear their PPE when working at heights"
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Tip: Use specific percentages and peer comparisons for maximum impact
              </p>
            </div>
          </div>

          {renderTitleXpFields()}
        </motion.div>
      );
    }

    if (category === 'recognition') {
      return (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="space-y-6 max-w-2xl mx-auto"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Recognition Setup</h2>
            <p className="text-muted-foreground">Acknowledge an achievement</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Achievement Type</Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                {ACHIEVEMENT_TYPES.map((type) => (
                  <Card
                    key={type.id}
                    className={`p-3 cursor-pointer text-center ${recognitionContent.achievementType === type.id ? 'ring-2 ring-primary' : ''}`}
                    onClick={() => setRecognitionContent({ ...recognitionContent, achievementType: type.id as any })}
                  >
                    <p className="font-medium">{type.label}</p>
                    <p className="text-xs text-muted-foreground">{type.description}</p>
                  </Card>
                ))}
              </div>
            </div>

            <div>
              <Label>Badge Icon</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {BADGE_ICONS.map((icon) => (
                  <Button
                    key={icon}
                    variant={recognitionContent.badgeIcon === icon ? 'default' : 'outline'}
                    size="icon"
                    className="text-xl"
                    onClick={() => setRecognitionContent({ ...recognitionContent, badgeIcon: icon })}
                  >
                    {icon}
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <Label>Recognition Reason *</Label>
              <Textarea
                value={recognitionContent.reason}
                onChange={(e) => setRecognitionContent({ ...recognitionContent, reason: e.target.value })}
                placeholder="e.g., For completing 30 days of consistent safety check-ins"
                rows={3}
              />
            </div>
          </div>

          {renderTitleXpFields()}
        </motion.div>
      );
    }

    if (category === 'success_story') {
      return (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="space-y-6 max-w-2xl mx-auto"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Eye className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Success Story Setup</h2>
            <p className="text-muted-foreground">Share a peer's safety success</p>
          </div>

          <div className="space-y-4">
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Share Anonymously</Label>
                  <p className="text-sm text-muted-foreground">Hide the worker's name</p>
                </div>
                <Switch
                  checked={successStoryContent.isAnonymous}
                  onCheckedChange={(checked) => setSuccessStoryContent({ ...successStoryContent, isAnonymous: checked })}
                />
              </div>
            </Card>

            {!successStoryContent.isAnonymous && (
              <div>
                <Label>Peer's Name</Label>
                <Input
                  value={successStoryContent.peerName}
                  onChange={(e) => setSuccessStoryContent({ ...successStoryContent, peerName: e.target.value })}
                  placeholder="Enter the worker's name"
                />
              </div>
            )}

            <div>
              <Label>Story Description *</Label>
              <Textarea
                value={successStoryContent.storyDescription}
                onChange={(e) => setSuccessStoryContent({ ...successStoryContent, storyDescription: e.target.value })}
                placeholder="Describe how this person improved safety or demonstrated excellent safety behavior..."
                rows={4}
              />
            </div>

            <div>
              <Label>Photo URL (Optional)</Label>
              <Input
                value={successStoryContent.photoUrl}
                onChange={(e) => setSuccessStoryContent({ ...successStoryContent, photoUrl: e.target.value })}
                placeholder="https://..."
              />
            </div>
          </div>

          {renderTitleXpFields()}
        </motion.div>
      );
    }

    if (category === 'video_message') {
      return (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="space-y-6 max-w-2xl mx-auto"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Video className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Video Message Setup</h2>
            <p className="text-muted-foreground">Share a video with workers</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Video Title</Label>
              <Input
                value={videoMessageContent.videoTitle}
                onChange={(e) => setVideoMessageContent({ ...videoMessageContent, videoTitle: e.target.value })}
                placeholder="Enter video title"
              />
            </div>

            <div>
              <Label>Video URL (YouTube) *</Label>
              <Input
                value={videoMessageContent.videoUrl}
                onChange={(e) => setVideoMessageContent({ ...videoMessageContent, videoUrl: e.target.value })}
                placeholder="https://youtube.com/watch?v=..."
              />
            </div>

            <div>
              <Label>Thumbnail URL (Optional)</Label>
              <Input
                value={videoMessageContent.thumbnailUrl}
                onChange={(e) => setVideoMessageContent({ ...videoMessageContent, thumbnailUrl: e.target.value })}
                placeholder="https://..."
              />
            </div>

            <div>
              <Label>Call to Action Text</Label>
              <Input
                value={videoMessageContent.callToAction}
                onChange={(e) => setVideoMessageContent({ ...videoMessageContent, callToAction: e.target.value })}
                placeholder="e.g., Watch to learn about ladder safety"
              />
            </div>
          </div>

          {renderTitleXpFields()}
        </motion.div>
      );
    }

    return null;
  };

  const renderContentStep = () => {
    // Banner-specific content step - only message with character limit
    if (deliveryMethod === 'banner') {
      const maxChars = 80;
      return (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="space-y-6 max-w-2xl mx-auto"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Mail className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Banner Message</h2>
            <p className="text-muted-foreground">This message will replace "Apa yang bisa saya bantu hari ini?"</p>
          </div>

          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Message *</Label>
                <span className={`text-sm ${message.length > maxChars ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {message.length}/{maxChars}
                </span>
              </div>
              <Textarea
                placeholder="e.g., Ingat, keselamatan adalah tanggung jawab kita bersama!"
                value={message}
                onChange={(e) => {
                  if (e.target.value.length <= maxChars) {
                    setMessage(e.target.value);
                  }
                }}
                rows={3}
                className="resize-none"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Keep the message short and impactful for better visibility
              </p>
            </div>

            {/* Preview */}
            <Card className="p-4 bg-muted/30">
              <Label className="text-xs text-muted-foreground mb-2 block">Preview</Label>
              <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-4 text-center">
                <p className="text-sm font-medium">{message || 'Apa yang bisa saya bantu hari ini?'}</p>
              </div>
            </Card>
          </div>
        </motion.div>
      );
    }

    // Default content step for non-setup categories (shouldn't happen with current setup)
    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="space-y-6 max-w-2xl mx-auto"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="h-8 w-8 text-primary" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Create Content</h2>
          <p className="text-muted-foreground">What message do you want to deliver?</p>
        </div>

        {/* AI Generation Option */}
        <Card className="p-4 border-dashed">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Wand2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">AI Content Generation</p>
                <p className="text-sm text-muted-foreground">Generate personalized content</p>
              </div>
            </div>
            <Switch checked={useAiContent} onCheckedChange={setUseAiContent} />
          </div>
          
          {useAiContent && (
            <div className="space-y-3 mt-4">
              <Textarea
                placeholder="Describe what you want to communicate..."
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                rows={3}
              />
              <Button 
                onClick={handleGenerateContent} 
                disabled={generatingContent}
                className="w-full"
              >
                {generatingContent ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Content
                  </>
                )}
              </Button>
            </div>
          )}
        </Card>

        <div className="space-y-4">
          <div>
            <Label>Title *</Label>
            <Input placeholder="Enter title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          <div>
            <Label>Message</Label>
            <Textarea
              placeholder="Enter message content"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
            />
          </div>

          <div>
            <Label>XP Reward</Label>
            <Input
              type="number"
              value={xpReward}
              onChange={(e) => setXpReward(parseInt(e.target.value) || 0)}
              min={0}
              max={100}
            />
          </div>
        </div>
      </motion.div>
    );
  };

  const renderStep = () => {
    // Step 1: Delivery Method
    if (step === 1) {
      return (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="space-y-6"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Choose Delivery Method</h2>
            <p className="text-muted-foreground">How should this intervention be delivered?</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {DELIVERY_METHODS.map((method) => (
              <Card
                key={method.id}
                className={`p-6 cursor-pointer transition-all ${
                  deliveryMethod === method.id ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'
                }`}
                onClick={() => setDeliveryMethod(method.id)}
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                    {method.icon}
                  </div>
                  <span className="text-xl font-semibold">{method.label}</span>
                </div>
                <p className="text-muted-foreground">{method.description}</p>
              </Card>
            ))}
          </div>
        </motion.div>
      );
    }

    // Step 2: Category
    if (step === 2) {
      return (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="space-y-6"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Target className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Choose Category</h2>
            <p className="text-muted-foreground">
              {deliveryMethod === 'popup' ? 'Popup categories (auto-show on home)' : 
               deliveryMethod === 'quest' ? 'Quest categories (gamified tasks)' : 
               'Banner categories'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {getCategories(deliveryMethod).map((cat) => (
              <Card
                key={cat.id}
                className={`p-4 cursor-pointer transition-all ${
                  category === cat.id ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'
                }`}
                onClick={() => handleCategorySelect(cat.id)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                    {cat.icon}
                  </div>
                  <div>
                    <p className="font-medium">{cat.label}</p>
                    <p className="text-xs text-muted-foreground">{cat.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </motion.div>
      );
    }

    // For categories with setup: step 3 = setup, step 4 = target, step 5 = timing
    // For categories without setup: step 3 = target, step 4 = content, step 5 = timing
    if (categoryNeedsSetup) {
      if (step === 3) return renderSetupStep();
      if (step === 4) return renderTargetStep();
      if (step === 5) return renderTimingStep();
    } else {
      if (step === 3) return renderTargetStep();
      if (step === 4) return renderContentStep();
      if (step === 5) return renderTimingStep();
    }

    return null;
  };

  const renderTargetStep = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Users className="h-8 w-8 text-primary" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Choose Target Audience</h2>
        <p className="text-muted-foreground">Who should receive this intervention?</p>
      </div>

      <div className="max-w-xl mx-auto">
        <RadioGroup value={targetType} onValueChange={setTargetType} className="space-y-3">
          {TARGET_TYPES.map((type) => (
            <Label 
              key={type.id}
              className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-colors ${
                targetType === type.id ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'
              }`}
            >
              <RadioGroupItem value={type.id} />
              {type.icon}
              <span className="font-medium">{type.label}</span>
            </Label>
          ))}
        </RadioGroup>

        {targetType === 'individual' && (
          <Select value={targetWorkerId} onValueChange={setTargetWorkerId}>
            <SelectTrigger className="mt-4">
              <SelectValue placeholder="Select worker" />
            </SelectTrigger>
            <SelectContent>
              {workers.map((worker) => (
                <SelectItem key={worker.id} value={worker.id}>
                  {worker.display_name} ({worker.team})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {targetType === 'cluster' && (
          <Select value={targetClusterId} onValueChange={setTargetClusterId}>
            <SelectTrigger className="mt-4">
              <SelectValue placeholder="Select cluster" />
            </SelectTrigger>
            <SelectContent>
              {clusters.map((cluster) => (
                <SelectItem key={cluster.id} value={cluster.id}>
                  {cluster.cluster_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {targetType === 'team' && (
          <Select value={targetTeam} onValueChange={setTargetTeam}>
            <SelectTrigger className="mt-4">
              <SelectValue placeholder="Select team" />
            </SelectTrigger>
            <SelectContent>
              {teams.map((team) => (
                <SelectItem key={team} value={team}>
                  {team}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </motion.div>
  );

  const renderTimingStep = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6 max-w-2xl mx-auto"
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Clock className="h-8 w-8 text-primary" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Delivery Timing</h2>
        <p className="text-muted-foreground">When should this intervention be delivered?</p>
      </div>

      <RadioGroup value={deliveryTiming} onValueChange={(v) => setDeliveryTiming(v as 'now' | 'scheduled')}>
        <div className="grid md:grid-cols-2 gap-4">
          <Card 
            className={`p-6 cursor-pointer transition-all ${
              deliveryTiming === 'now' ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'
            }`}
            onClick={() => setDeliveryTiming('now')}
          >
            <div className="flex items-center gap-3">
              <RadioGroupItem value="now" id="now" />
              <div className="flex items-center gap-3">
                <Clock className="h-6 w-6 text-primary" />
                <div>
                  <Label htmlFor="now" className="font-semibold cursor-pointer">Deliver Now</Label>
                  <p className="text-sm text-muted-foreground">Send immediately</p>
                </div>
              </div>
            </div>
          </Card>
          
          <Card 
            className={`p-6 cursor-pointer transition-all ${
              deliveryTiming === 'scheduled' ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'
            }`}
            onClick={() => setDeliveryTiming('scheduled')}
          >
            <div className="flex items-center gap-3">
              <RadioGroupItem value="scheduled" id="scheduled" />
              <div className="flex items-center gap-3">
                <Calendar className="h-6 w-6 text-primary" />
                <div>
                  <Label htmlFor="scheduled" className="font-semibold cursor-pointer">Schedule</Label>
                  <p className="text-sm text-muted-foreground">Choose date & time</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </RadioGroup>

      {deliveryTiming === 'scheduled' && (
        <Card className="p-4 space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div>
              <Label>Time</Label>
              <Input
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
              />
            </div>
          </div>
        </Card>
      )}
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin?tab=intervention')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Create Intervention</h1>
              <p className="text-sm text-muted-foreground">Step {step} of {totalSteps}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto">
            {stepLabels.map((label, index) => (
              <div
                key={label}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium shrink-0 ${
                  step > index + 1 ? 'bg-success text-success-foreground' :
                  step === index + 1 ? 'bg-primary text-primary-foreground' :
                  'bg-muted text-muted-foreground'
                }`}
              >
                {step > index + 1 ? <CheckCircle className="h-4 w-4" /> : index + 1}
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <AnimatePresence mode="wait">
          {renderStep()}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-background/80 backdrop-blur-xl border-t px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button variant="outline" onClick={() => step > 1 ? setStep(step - 1) : navigate('/admin?tab=intervention')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {step === 1 ? 'Cancel' : 'Back'}
          </Button>

          {step < totalSteps ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canProceed()}>
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading || !canProceed()}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {deliveryTiming === 'scheduled' ? 'Schedule' : 'Create'}
                </>
              )}
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
}
